import { getPasswordForJob } from '~/query'
import type { CommonQueryParamsWithForeignAuth, HostType } from '~/query/api'
import { buildCommonParams, buildRequestOptions, getForeignAuthParam } from '~/query/api'

import type { AxiosRequestOptions } from '@capture/client-api/src/orval/customInstance'

import type { ServiceDict } from '../externals'

export type RequestContext = {
    commonParams: CommonQueryParamsWithForeignAuth
    requestOptions: AxiosRequestOptions
}

/**
 * Base class for API services.
 *
 * All services extending this class use a consistent constructor signature:
 * - services: ServiceDict - Required service endpoints configuration
 * - authToken: string | undefined - Authentication token (optional for public access)
 *
 * Services that don't require authentication (e.g., LoginService) should not extend this class.
 */
export class APIService {
    protected readonly services: ServiceDict
    protected readonly authToken: string | undefined

    constructor(
        protected readonly hostType: HostType,
        services: ServiceDict,
        authToken: string | undefined,
    ) {
        this.services = services
        this.authToken = authToken
    }

    /**
     * Get request context for API calls.
     *
     * @param jobID - Optional job ID. If provided, retrieves job-specific password for foreign auth.
     * @returns commonParams and requestOptions for API calls
     */
    protected getRequestContext(jobID?: JobID): RequestContext {
        const password = jobID ? getPasswordForJob(jobID) : undefined

        return {
            commonParams: buildCommonParams(getForeignAuthParam(password)),
            requestOptions: buildRequestOptions({
                hostType: this.hostType,
                authToken: this.authToken,
                serviceDict: this.services,
                password,
            }),
        }
    }
}
